import { Injectable, ElementRef, OnDestroy } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { VehicleZone, VehicleDocument, AdditionalPhoto } from '../protocols/shared/interfaces/base-protocol.interface';
import { DateFormatLocalizationService } from 'src/app/core/services/date-format-localization.service';

export type PhotoTargetType = 'zone' | 'document' | 'additional';
export const CAMERA_FILE_FALLBACK_ERROR = 'USE_FILE_INPUT_FALLBACK';

export interface PhotoConfig {
    type: PhotoTargetType;
    target: { id: string; name: string; index?: number };
    autoNavigate?: boolean;
}

export interface PhotoResult {
    blob: Blob;
    url: string;
    timestamp: Date;
    success: boolean;
    error?: string;
    cancelled?: boolean;
    mimeType?: string;
    base64DataUrl?: string;
}

export type Facing = 'environment' | 'user';

export interface CameraState {
    facing: Facing;
    deviceId?: string | null;
    stream: MediaStream | null;
    hasMultipleCameras: boolean;
    torchSupported: boolean;
    torchEnabled: boolean;
    error: string | null;
    isLoading: boolean;
    isOpen: boolean;
}

export interface PhotoItem {
    id?: string;
    name?: string;
    photographed?: boolean;
    photoUrl?: string;
    photoBlob?: Blob;
    timestamp?: Date;
}

export interface PhotoCaptureResult {
    success: boolean;
    photographed: boolean;
    photoUrl?: string;
    photoBlob?: Blob;
    base64?: string;
    timestamp?: Date;
    error?: string;
}

export interface PhotoCaptureOptions {
    onFormUpdate?: (id: string, value: string) => Promise<void>;
    onMessage?: (message: string, type: 'success' | 'error' | 'info') => void;
    onUpload?: (blob: Blob, fieldName: string) => Promise<string>;
}

interface RNMessagePayload {
    type?: string;
    requestId?: string;
    success?: boolean;
    cancelled?: boolean;
    error?: string;
    base64?: string;
    url?: string;
    mime?: string;
}

declare global {
    interface Window {
        ReactNativeWebView?: { postMessage: (message: string) => void };
    }
}

@Injectable({ providedIn: 'root' })
export class VehiclePhotoService implements OnDestroy {
    private readonly RN_RESPONSE_TIMEOUT_MS = 30_000;

    private cameraStateSubject = new BehaviorSubject<CameraState>({
        facing: 'environment',
        deviceId: null,
        stream: null,
        hasMultipleCameras: false,
        torchSupported: false,
        torchEnabled: false,
        error: null,
        isLoading: false,
        isOpen: false,
    });

    public cameraState$ = this.cameraStateSubject.asObservable();

    private videoElement: HTMLVideoElement | null = null;
    private canvasElement: HTMLCanvasElement | null = null;

    private pendingRequests = new Map<
        string,
        { resolve: (r: PhotoResult) => void; reject: (e: Error) => void; timeoutHandle: number }
    >();

    constructor(
        private translate: TranslateService,
        private dateFormatLocalization: DateFormatLocalizationService
    ) {
        if (typeof window !== 'undefined') {
            window.addEventListener('message', this.handleWindowMessage);
        }

        if (typeof document !== 'undefined') {
            document.addEventListener('message', this.handleWindowMessage as EventListener);
        }
    }


    ngOnDestroy(): void {
        if (typeof window !== 'undefined') {
            window.removeEventListener('message', this.handleWindowMessage);
        }
        if (typeof document !== 'undefined') {
            document.removeEventListener('message', this.handleWindowMessage as EventListener);
        }

        this.pendingRequests.forEach((v) => window.clearTimeout(v.timeoutHandle));
        this.pendingRequests.clear();
        this.stopCamera();
    }

    private setCameraState(patch: Partial<CameraState>): void {
        const current = this.cameraStateSubject.value;
        this.cameraStateSubject.next({ ...current, ...patch });
    }

    private generateRequestId(): string {
        return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    }

    private base64ToBlob(base64: string, mime = 'image/jpeg'): Blob {
        const binaryString = atob(base64);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) bytes[i] = binaryString.charCodeAt(i);
        return new Blob([bytes.buffer], { type: mime });
    }

    private dataUrlToBlob(dataUrl: string): Blob {
        const match = dataUrl.match(/^data:(.+);base64,(.*)$/);
        if (!match) return new Blob();
        const mime = match[1];
        const base64 = match[2];
        return this.base64ToBlob(base64, mime);
    }

    public blobToBase64(blob: Blob): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                if (typeof reader.result === 'string') {
                    resolve(reader.result);
                } else {
                    reject(new Error('FileReader result is not a string'));
                }
            };
            reader.onerror = () => reject(reader.error);
            reader.readAsDataURL(blob);
        });
    }

    public async fetchPhotoFromUrl(url: string): Promise<Blob> {
        try {
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`Failed to fetch photo: ${response.status} ${response.statusText}`);
            }
            return await response.blob();
        } catch (err) {
            const error = err instanceof Error ? err : new Error('Unknown fetch error');
            throw new Error(`fetchPhotoFromUrl error: ${error.message}`);
        }
    }

    public setVideoElements(video: ElementRef<HTMLVideoElement> | HTMLVideoElement, canvas: ElementRef<HTMLCanvasElement> | HTMLCanvasElement): void {
        if (video instanceof ElementRef) {
            this.videoElement = video.nativeElement;
        } else {
            this.videoElement = video;
        }

        if (canvas instanceof ElementRef) {
            this.canvasElement = canvas.nativeElement;
        } else {
            this.canvasElement = canvas;
        }
    }

    public async startCamera(config: PhotoConfig): Promise<boolean> {
        return this.startCameraViaBrowser(config);
    }

    public isReactNativeBridgeAvailable(): boolean {
        return typeof window !== 'undefined'
            && typeof window.ReactNativeWebView?.postMessage === 'function';
    }

    public async capturePhotoViaNativeBridge(config: PhotoConfig): Promise<PhotoResult> {
        if (!this.isReactNativeBridgeAvailable()) {
            return {
                blob: new Blob(),
                url: '',
                timestamp: new Date(),
                success: false,
                error: 'native_bridge_unavailable'
            };
        }

        this.setCameraState({ isLoading: true, error: null });
        try {
            const result = await this.startCameraViaReactNative(config);
            this.setCameraState({ isLoading: false, error: result.success ? null : (result.error ?? null) });
            return result;
        } catch (err) {
            const error = err instanceof Error ? err : new Error('native_capture_failed');
            this.setCameraState({ isLoading: false, error: error.message });
            return {
                blob: new Blob(),
                url: '',
                timestamp: new Date(),
                success: false,
                error: error.message
            };
        }
    }

    private async startCameraViaBrowser(_config: PhotoConfig): Promise<boolean> {
        try {
            this.setCameraState({ isLoading: true, error: null });
            this.stopCamera();
            await new Promise((r) => setTimeout(r, 100));

            if (typeof navigator === 'undefined' || !navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                this.setCameraState({ error: CAMERA_FILE_FALLBACK_ERROR, isLoading: false, isOpen: false });
                return false;
            }

            try {
                if (navigator.permissions && navigator.permissions.query) {
                    const status = (await (navigator.permissions.query({ name: 'camera' as PermissionName }) as Promise<PermissionStatus>));
                    if (status && status.state === 'denied') {
                        this.setCameraState({ error: CAMERA_FILE_FALLBACK_ERROR, isLoading: false, isOpen: false });
                        return false;
                    }
                }
            } catch {
                // Permissions API not available, continue trying getUserMedia
            }

            let constraints: MediaStreamConstraints = {
                video: { facingMode: this.cameraStateSubject.value.facing, width: { ideal: 1920, min: 640 }, height: { ideal: 1080, min: 480 } }
            };
            let stream: MediaStream;
            try {
                stream = await navigator.mediaDevices.getUserMedia(constraints);
            } catch {
                constraints = { video: { facingMode: this.cameraStateSubject.value.facing } };
                stream = await navigator.mediaDevices.getUserMedia(constraints);
            }

            this.setCameraState({ stream, isOpen: true, isLoading: false, error: null });
            await this.syncTorchSupportForStream(stream);

            if (this.videoElement) {
                this.videoElement.srcObject = stream;
                this.videoElement.onloadedmetadata = () => void this.videoElement?.play().catch((e) => console.warn('Video play error', e));
                await this.videoElement.play().catch((e) => console.warn('Video play error', e));
            }

            try {
                if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
                    const devices = await navigator.mediaDevices.enumerateDevices();
                    const videoCount = devices.filter((d) => d.kind === 'videoinput').length;
                    this.setCameraState({ hasMultipleCameras: videoCount > 1 });
                } else {
                    this.setCameraState({ hasMultipleCameras: false });
                }
            } catch {
                this.setCameraState({ hasMultipleCameras: false });
            }

            return true;
        } catch (err) {
            const e = err instanceof Error ? err : new Error('Unknown error starting camera');
            const errorName = (e as Error & { name?: string }).name ?? '';

            const isPermissionError =
                errorName === 'NotAllowedError' ||
                e.message.includes('Permission denied') ||
                e.message.includes('Permisos de cámara denegados');
            const isDeviceMissing = errorName === 'NotFoundError' || e.message.includes('Requested device not found');

            if (isPermissionError || isDeviceMissing) {
                this.setCameraState({
                    error: CAMERA_FILE_FALLBACK_ERROR,
                    isLoading: false,
                    isOpen: false,
                    torchSupported: false,
                    torchEnabled: false
                });
                console.warn('startCameraViaBrowser camera unavailable:', e);
            } else {
                this.setCameraState({
                    error: this.humanizeCameraError(e),
                    isLoading: false,
                    isOpen: false,
                    torchSupported: false,
                    torchEnabled: false
                });
                console.error('startCameraViaBrowser error:', e);
            }
            return false;
        }
    }

    private getActiveVideoTrack(stream: MediaStream | null = this.cameraStateSubject.value.stream): MediaStreamTrack | null {
        return stream?.getVideoTracks?.()[0] ?? null;
    }

    private async syncTorchSupportForStream(stream: MediaStream | null = this.cameraStateSubject.value.stream): Promise<void> {
        const track = this.getActiveVideoTrack(stream);
        if (!track) {
            this.setCameraState({ torchSupported: false, torchEnabled: false });
            return;
        }

        try {
            const capabilities = track.getCapabilities?.() as MediaTrackCapabilities & { torch?: boolean };
            const settings = track.getSettings?.() as MediaTrackSettings & { torch?: boolean };
            const torchSupported = capabilities?.torch === true;
            const torchEnabled = settings?.torch === true;
            this.setCameraState({ torchSupported, torchEnabled: torchSupported ? torchEnabled : false });
        } catch {
            this.setCameraState({ torchSupported: false, torchEnabled: false });
        }
    }

    public async refreshTorchSupport(): Promise<boolean> {
        await this.syncTorchSupportForStream();
        return this.cameraStateSubject.value.torchSupported;
    }

    public async setTorch(enabled: boolean): Promise<boolean> {
        const track = this.getActiveVideoTrack();
        if (!track) {
            this.setCameraState({ torchSupported: false, torchEnabled: false });
            return false;
        }

        try {
            const capabilities = track.getCapabilities?.() as MediaTrackCapabilities & { torch?: boolean };
            if (capabilities?.torch !== true) {
                this.setCameraState({ torchSupported: false, torchEnabled: false });
                return false;
            }

            const constraints = {
                advanced: [{ torch: enabled }]
            } as unknown as MediaTrackConstraints;
            await track.applyConstraints(constraints);

            await this.syncTorchSupportForStream();
            this.setCameraState({ torchSupported: true, torchEnabled: enabled });
            return true;
        } catch {
            await this.syncTorchSupportForStream();
            return false;
        }
    }

    public async toggleTorch(): Promise<boolean> {
        const current = this.cameraStateSubject.value;
        if (!current.torchSupported) return false;
        return this.setTorch(!current.torchEnabled);
    }

    private startCameraViaReactNative(config: PhotoConfig): Promise<PhotoResult> {
        const requestId = this.generateRequestId();
        return new Promise<PhotoResult>((resolve, reject) => {
            const timeoutHandle = window.setTimeout(() => {
                this.pendingRequests.delete(requestId);
                resolve({ blob: new Blob(), url: '', timestamp: new Date(), success: false, error: 'timeout' });
            }, this.RN_RESPONSE_TIMEOUT_MS);

            this.pendingRequests.set(requestId, { resolve, reject, timeoutHandle });

            const payload = { type: 'startCamera', requestId, config };
            try {
                window.ReactNativeWebView?.postMessage(JSON.stringify(payload));
            } catch (postErr) {
                window.clearTimeout(timeoutHandle);
                this.pendingRequests.delete(requestId);
                reject(new Error('failed_postMessage'));
            }
        });
    }

    private handleWindowMessage = (ev: MessageEvent): void => {
        if (!ev || !ev.data) return;

        let payload: unknown = ev.data;
        if (typeof payload === 'string') {
            try { payload = JSON.parse(payload); } catch { return; }
        }
        if (!payload || typeof payload !== 'object') return;

        const p = payload as RNMessagePayload;
        const type = typeof p.type === 'string' ? p.type : undefined;
        const requestId = typeof p.requestId === 'string' ? p.requestId : undefined;
        if (!type || !requestId) return;

        const pending = this.pendingRequests.get(requestId);
        if (!pending) return;

        try {
            if (type === 'photoCaptured') {
                const success = typeof p.success === 'boolean' ? p.success : false;
                if (!success) {
                    const cancelled = Boolean(p.cancelled);
                    if (cancelled) {
                        pending.resolve({ blob: new Blob(), url: '', timestamp: new Date(), success: false, cancelled: true });
                    } else {
                        pending.resolve({ blob: new Blob(), url: '', timestamp: new Date(), success: false, error: p.error ?? 'unknown_error' });
                    }
                    return;
                }

                const base64 = typeof p.base64 === 'string' ? p.base64 : undefined;
                const url = typeof p.url === 'string' ? p.url : '';
                const mime = typeof p.mime === 'string' ? p.mime : 'image/jpeg';

                if (base64) {
                    const dataUrl = base64.startsWith('data:') ? base64 : `data:${mime};base64,${base64}`;
                    const blob = this.dataUrlToBlob(dataUrl);
                    pending.resolve({ blob, url: URL.createObjectURL(blob), timestamp: new Date(), success: true, mimeType: mime, base64DataUrl: dataUrl });
                    return;
                }

                if (url) {
                    if (url.startsWith('data:')) {
                        const blob = this.dataUrlToBlob(url);
                        pending.resolve({ blob, url: URL.createObjectURL(blob), timestamp: new Date(), success: true, mimeType: mime });
                        return;
                    }
                    // remote url (file:// or http(s))
                    pending.resolve({ blob: new Blob(), url, timestamp: new Date(), success: true, mimeType: mime });
                    return;
                }

                pending.resolve({ blob: new Blob(), url: '', timestamp: new Date(), success: false, error: 'no_payload' });
            } else if (type === 'photoCancel') {
                pending.resolve({ blob: new Blob(), url: '', timestamp: new Date(), success: false, cancelled: true });
            }
        } finally {
            window.clearTimeout(pending.timeoutHandle);
            this.pendingRequests.delete(requestId);
        }
    };

    public async capturePhoto(): Promise<PhotoResult> {
        const hasActiveStream = !!this.cameraStateSubject.value.stream;

        if (!this.videoElement || !this.canvasElement || !hasActiveStream) {
            return { blob: new Blob(), url: '', timestamp: new Date(), success: false, error: 'Cámara no inicializada' };
        }

        try {
            const ctx = this.canvasElement.getContext('2d');
            if (!ctx) throw new Error('No se pudo obtener contexto del canvas');

            const width = this.videoElement.videoWidth || this.videoElement.clientWidth || 640;
            const height = this.videoElement.videoHeight || this.videoElement.clientHeight || 480;
            this.canvasElement.width = width;
            this.canvasElement.height = height;

            ctx.drawImage(this.videoElement, 0, 0, width, height);

            const blob: Blob = await new Promise<Blob>((resolve) =>
                this.canvasElement!.toBlob((b) => resolve(b || new Blob()), 'image/jpeg', 0.85)
            );

            const url = URL.createObjectURL(blob);

            return { blob, url, timestamp: new Date(), success: true, mimeType: blob.type };
        } catch (err) {
            const e = err instanceof Error ? err : new Error('Error al capturar foto');
            console.error('capturePhoto error:', e);
            return { blob: new Blob(), url: '', timestamp: new Date(), success: false, error: e.message };
        }
    }

    public async createPhotoResultFromFile(file: File): Promise<PhotoResult> {
        try {
            const blob = file;
            const url = URL.createObjectURL(blob);
            return { blob, url, timestamp: new Date(), success: true, mimeType: blob.type };
        } catch (err) {
            const e = err instanceof Error ? err : new Error('Error al procesar fichero');
            return { blob: new Blob(), url: '', timestamp: new Date(), success: false, error: e.message };
        }
    }

    public async capturePhotoFromFile(file: File): Promise<PhotoResult> {
        return this.createPhotoResultFromFile(file);
    }

    public async switchCamera(): Promise<boolean> {
        const current = this.cameraStateSubject.value;
        if (!current.hasMultipleCameras) return false;
        const newFacing: 'environment' | 'user' = current.facing === 'environment' ? 'user' : 'environment';
        this.setCameraState({ facing: newFacing });
        const config: PhotoConfig = { type: 'zone', target: { id: 'switch-temp', name: 'switch-temp' } };
        return this.startCamera(config);
    }

    public stopCamera(): void {
        const current = this.cameraStateSubject.value;
        if (current.stream) current.stream.getTracks().forEach((t) => t.stop());
        if (this.videoElement) {
            try {
                this.videoElement.srcObject = null;
                this.videoElement.pause();
                this.videoElement.removeAttribute('src');
            } catch {
                // noop
            }
        }
        this.setCameraState({ isOpen: false, isLoading: false, error: null, stream: null });
        this.setCameraState({ torchSupported: false, torchEnabled: false });
    }

    public resetCameraState(): void {
        this.stopCamera();
        this.cameraStateSubject.next({
            facing: 'environment',
            deviceId: null,
            stream: null,
            hasMultipleCameras: false,
            torchSupported: false,
            torchEnabled: false,
            error: null,
            isLoading: false,
            isOpen: false,
        });
    }

    public async diagnoseCameraIssues(): Promise<{ hasCamera: boolean; permissionStatus: 'granted' | 'denied' | 'prompt' | 'not_supported' | 'unknown' | 'error'; availableCameras: number; error?: string }> {
        try {
            if (typeof navigator === 'undefined' || !navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                return { hasCamera: false, permissionStatus: 'not_supported', availableCameras: 0, error: 'getUserMedia no soportado' };
            }

            let permissionStatus: 'granted' | 'denied' | 'prompt' | 'unknown' = 'unknown';
            try {
                if (navigator.permissions && navigator.permissions.query) {
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                    const perm = (await (navigator.permissions.query({ name: 'camera' as PermissionName }) as Promise<PermissionStatus>));
                    permissionStatus = perm.state === 'granted' ? 'granted' : perm.state === 'denied' ? 'denied' : 'prompt';
                }
            } catch {
                permissionStatus = 'unknown';
            }

            let available = 0;
            try {
                if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
                    const devices = await navigator.mediaDevices.enumerateDevices();
                    available = devices.filter((d) => d.kind === 'videoinput').length;
                }
            } catch {
                available = 0;
            }

            return { hasCamera: available > 0, permissionStatus, availableCameras: available };
        } catch (err) {
            const e = err instanceof Error ? err : new Error('diagnose failed');
            return { hasCamera: false, permissionStatus: 'error', availableCameras: 0, error: e.message };
        }
    }

    public async testCameraAccess(): Promise<boolean> {
        if (typeof navigator === 'undefined' || !navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) return false;
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
            stream.getTracks().forEach((t) => t.stop());
            return true;
        } catch {
            return false;
        }
    }

    public getCameraState(): CameraState {
        return { ...this.cameraStateSubject.value };
    }

    setLoading(isLoading: boolean) {
        const current = this.getCameraState();
        this.cameraStateSubject.next({ ...current, isLoading });
    }

    setError(error: string | null) {
        this.setCameraState({ error });
    }

    setOpen(isOpen: boolean) {
        this.setCameraState({ isOpen });
    }

    setStream(stream: MediaStream | null) {
        this.setCameraState({ stream });
    }

    setDeviceId(deviceId: string | null) {
        this.setCameraState({ deviceId });
    }

    setFacing(facing: Facing) {
        this.setCameraState({ facing });
    }

    setHasMultipleCameras(hasMultiple: boolean) {
        this.setCameraState({ hasMultipleCameras: hasMultiple });
    }

    public revokeObjectURL(url: string): void {
        try {
            if (url && url.startsWith('blob:')) URL.revokeObjectURL(url);
        } catch {
            // noop
        }
    }

    /** Clean photo data (generic) */
    public cleanPhotoData<T extends Partial<PhotoItem> | undefined>(photoObject: T): void {
        if (!photoObject) return;
        if (typeof photoObject.photoUrl === 'string' && photoObject.photoUrl.startsWith('blob:')) {
            try {
                URL.revokeObjectURL(photoObject.photoUrl);
            } catch {
                // noop
            }
        }
        photoObject.photographed = false;
        photoObject.photoUrl = undefined;
        photoObject.photoBlob = undefined;
        photoObject.timestamp = undefined;
    }

    public countPhotographedItems<T extends Partial<PhotoItem>>(items?: T[] | null): number {
        if (!items || !Array.isArray(items)) return 0;
        return items.filter((item) => Boolean(item?.photographed) && typeof item?.photoUrl === 'string').length;
    }

    public hasAnyPhotos<
        TZone extends Partial<PhotoItem> = Partial<PhotoItem>,
        TAdd = unknown,
        TDoc extends Partial<PhotoItem> = Partial<PhotoItem>
    >(vehicleZones?: TZone[] | null, additionalPhotos?: TAdd[] | null, vehicleDocuments?: TDoc[] | null): boolean {
        const hasVehiclePhotos = this.countPhotographedItems(vehicleZones) > 0;
        const hasAdditionalPhotos = (additionalPhotos?.length ?? 0) > 0;
        const hasDocumentPhotos = this.countPhotographedItems(vehicleDocuments) > 0;
        return hasVehiclePhotos || hasAdditionalPhotos || hasDocumentPhotos;
    }

    public getTotalPhotosCount<
        TZone extends Partial<PhotoItem> = Partial<PhotoItem>,
        TAdd = unknown,
        TDoc extends Partial<PhotoItem> = Partial<PhotoItem>
    >(vehicleZones?: TZone[] | null, additionalPhotos?: TAdd[] | null, vehicleDocuments?: TDoc[] | null): number {
        const vehiclePhotosCount = this.countPhotographedItems(vehicleZones);
        const additionalPhotosCount = additionalPhotos?.length ?? 0;
        const documentPhotosCount = this.countPhotographedItems(vehicleDocuments);
        return vehiclePhotosCount + additionalPhotosCount + documentPhotosCount;
    }

    public findNextUnphotographedItem<T extends { photographed?: boolean } = { photographed?: boolean }>(items?: T[] | null, currentIndex = -1): number {
        if (!items || items.length === 0) return -1;
        for (let i = currentIndex + 1; i < items.length; i++) {
            if (!items[i].photographed) return i;
        }
        for (let i = 0; i <= currentIndex; i++) {
            if (!items[i].photographed) return i;
        }
        return -1;
    }

    public getNextUnphotographedZone<T extends Partial<PhotoItem> = Partial<PhotoItem>>(vehicleZones?: T[] | null, currentIndex = -1): { index: number; zone: T | null } {
        const idx = this.findNextUnphotographedItem(vehicleZones, currentIndex);
        return { index: idx, zone: idx === -1 || !vehicleZones ? null : vehicleZones[idx] };
    }

    public getNextUnphotographedDocument<T extends Partial<PhotoItem> = Partial<PhotoItem>>(vehicleDocuments?: T[] | null, currentIndex = -1): { index: number; document: T | null } {
        const idx = this.findNextUnphotographedItem(vehicleDocuments, currentIndex);
        return { index: idx, document: idx === -1 || !vehicleDocuments ? null : vehicleDocuments[idx] };
    }

    public getCameraSwitchText(): string {
        const facing = this.cameraStateSubject.value.facing;
        return facing === 'environment' ? this.translate.instant('protocol_orders.switchToFrontCamera') : this.translate.instant('protocol_orders.switchToBackCamera');
    }

    private humanizeCameraError(error: Error): string {
        const name = (error as Error & { name?: string }).name ?? '';
        if (name === 'NotReadableError') return this.translate.instant('protocol_orders.cameraInUse') || 'La cámara está siendo utilizada por otra aplicación';
        if (name === 'NotAllowedError') return this.translate.instant('protocol_orders.cameraPermissionDenied') || 'Permisos de cámara denegados';
        if (name === 'NotFoundError') return this.translate.instant('protocol_orders.noCameraFound') || 'No se encontró ninguna cámara';
        if (name === 'OverconstrainedError') return this.translate.instant('protocol_orders.cameraNotCompatible') || 'La cámara no cumple los requisitos solicitados';
        return error.message || this.translate.instant('protocol_orders.cameraAccessError') || 'Error de cámara';
    }

    public async captureZonePhoto(
        zone: VehicleZone,
        photoBlob: Blob | null | undefined,
        photoUrl: string,
        options?: PhotoCaptureOptions
    ): Promise<PhotoCaptureResult> {
        const blob: Blob | null = photoBlob && photoBlob.size > 0 ? photoBlob : null;

        // Inicializar resultado
        const result: PhotoCaptureResult = {
            success: false,
            photographed: false,
        };

        try {
            zone.photographed = true;
            zone.timestamp = new Date();
            result.photographed = true;
            result.timestamp = zone.timestamp;

            // Caso 1: Tenemos blob directamente
            if (blob) {
                zone.photoBlob = blob;
                zone.photoUrl = photoUrl || URL.createObjectURL(blob);
                result.photoBlob = blob;
                result.photoUrl = zone.photoUrl;

                try {
                    const formValue = options?.onUpload
                        ? await options.onUpload(blob, zone.id)
                        : await this.blobToBase64(blob);
                    result.base64 = formValue;

                    if (options?.onFormUpdate) {
                        await options.onFormUpdate(zone.id, formValue);
                    }
                } catch (err) {
                    if (photoUrl) {
                        zone.photoUrl = photoUrl;
                        result.photoUrl = photoUrl;
                    }
                    result.error = 'Error processing photo';
                }

                result.success = true;
                if (options?.onMessage) {
                    options.onMessage(
                        this.translate.instant('protocol_orders.zoneCapturedSuccessfully', { zone: zone.name }),
                        'success'
                    );
                }
                return result;
            }

            // Caso 2: Solo tenemos URL, intentar fetch
            if (photoUrl) {
                try {
                    const fetchedBlob = await this.fetchPhotoFromUrl(photoUrl);
                    zone.photoBlob = fetchedBlob;
                    zone.photoUrl = URL.createObjectURL(fetchedBlob);
                    result.photoBlob = fetchedBlob;
                    result.photoUrl = zone.photoUrl;

                    try {
                        const formValue = options?.onUpload
                            ? await options.onUpload(fetchedBlob, zone.id)
                            : await this.blobToBase64(fetchedBlob);
                        result.base64 = formValue;

                        if (options?.onFormUpdate) {
                            await options.onFormUpdate(zone.id, formValue);
                        }
                    } catch (err) {
                        result.error = 'Error processing fetched photo';
                    }

                    result.success = true;
                    return result;
                } catch (err) {
                    zone.photoUrl = photoUrl;
                    result.photoUrl = photoUrl;
                    result.error = 'Error fetching photo from URL';
                    result.success = true;

                    if (options?.onMessage) {
                        options.onMessage(
                            this.translate.instant('protocol_orders.photoSavedAsUrl') || 'Foto guardada como URL (no en DTO).',
                            'info'
                        );
                    }
                    return result;
                }
            }

            // Caso 3: No hay ni blob ni URL
            result.error = 'No blob or URL provided';
            return result;

        } catch (err) {
            result.error = err instanceof Error ? err.message : 'Unknown error';
            result.success = false;
            return result;
        }
    }

    public async captureDocumentPhoto(
        document: VehicleDocument,
        photoBlob: Blob | null | undefined,
        photoUrl: string,
        options?: PhotoCaptureOptions
    ): Promise<PhotoCaptureResult> {
        const blob: Blob | null = photoBlob && photoBlob.size > 0 ? photoBlob : null;

        const result: PhotoCaptureResult = {
            success: false,
            photographed: false,
        };

        try {
            document.photographed = true;
            document.timestamp = new Date();
            result.photographed = true;
            result.timestamp = document.timestamp;

            // Caso 1: Tenemos blob directamente
            if (blob) {
                document.photoBlob = blob;
                document.photoUrl = photoUrl || URL.createObjectURL(blob);
                result.photoBlob = blob;
                result.photoUrl = document.photoUrl;

                try {
                    const formValue = options?.onUpload
                        ? await options.onUpload(blob, document.id)
                        : await this.blobToBase64(blob);
                    result.base64 = formValue;

                    if (options?.onFormUpdate) {
                        await options.onFormUpdate(document.id, formValue);
                    }
                } catch (err) {
                    result.error = 'Error processing photo';
                }

                result.success = true;
                if (options?.onMessage) {
                    options.onMessage(
                        this.translate.instant('protocol_orders.documentCapturedSuccessfully', { document: document.name }),
                        'success'
                    );
                }
                return result;
            }

            // Caso 2: Solo tenemos URL, intentar fetch
            if (photoUrl) {
                try {
                    const fetchedBlob = await this.fetchPhotoFromUrl(photoUrl);
                    document.photoBlob = fetchedBlob;
                    document.photoUrl = URL.createObjectURL(fetchedBlob);
                    result.photoBlob = fetchedBlob;
                    result.photoUrl = document.photoUrl;

                    try {
                        const formValue = options?.onUpload
                            ? await options.onUpload(fetchedBlob, document.id)
                            : await this.blobToBase64(fetchedBlob);
                        result.base64 = formValue;

                        if (options?.onFormUpdate) {
                            await options.onFormUpdate(document.id, formValue);
                        }
                    } catch (err) {
                        result.error = 'Error processing fetched photo';
                    }

                    result.success = true;
                    return result;
                } catch (err) {
                    document.photoUrl = photoUrl;
                    result.photoUrl = photoUrl;
                    result.error = 'Error fetching photo from URL';
                    result.success = true;

                    if (options?.onMessage) {
                        options.onMessage(
                            this.translate.instant('protocol_orders.photoSavedAsUrl') || 'Foto guardada como URL (no en DTO).',
                            'info'
                        );
                    }
                    return result;
                }
            }

            result.error = 'No blob or URL provided';
            return result;

        } catch (err) {
            result.error = err instanceof Error ? err.message : 'Unknown error';
            result.success = false;
            return result;
        }
    }

    public async captureAdditionalPhoto(
        description: string,
        photoBlob: Blob | null | undefined,
        photoUrl: string,
        options?: PhotoCaptureOptions
    ): Promise<AdditionalPhoto> {
        const blob: Blob | null = photoBlob && photoBlob.size > 0 ? photoBlob : null;
        const timestamp = new Date();
        const id = `additional-${timestamp.getTime()}`;

        const newPhoto: AdditionalPhoto = {
            id,
            description: description,
            url: photoUrl || '',
            blob: undefined,
            timestamp
        };

        // Caso 1: Tenemos blob directamente
        if (blob) {
            newPhoto.blob = blob;
            newPhoto.url = photoUrl || URL.createObjectURL(blob);

            try {
                newPhoto.imageSrc = options?.onUpload
                    ? await options.onUpload(blob, id)
                    : await this.blobToBase64(blob);
            } catch (err) {
            }

            if (options?.onMessage) {
                options.onMessage(
                    this.translate.instant('protocol_orders.additionalPhotoCaptured', { description: newPhoto.description }),
                    'success'
                );
            }

            return newPhoto;
        }

        // Caso 2: Solo tenemos URL, intentar fetch
        if (photoUrl) {
            try {
                const fetchedBlob = await this.fetchPhotoFromUrl(photoUrl);
                newPhoto.blob = fetchedBlob;
                newPhoto.url = URL.createObjectURL(fetchedBlob);

                try {
                    newPhoto.imageSrc = options?.onUpload
                        ? await options.onUpload(fetchedBlob, id)
                        : await this.blobToBase64(fetchedBlob);
                } catch (err) {
                }
            } catch (err) {
                newPhoto.url = photoUrl;
            }
        }

        return newPhoto;
    }

    getPhotographedZonesCount(vehicleZones: VehicleZone[]): number {
        return vehicleZones.filter(zone => zone.photographed).length;
    }

    getTotalZonesCount(vehicleZones: VehicleZone[]): number {
        return vehicleZones.length;
    }

    getPendingZonesCount(vehicleZones: VehicleZone[]): number {
        return vehicleZones.filter(zone => !zone.photographed).length;
    }

    resetVehicleZonePhotos(vehicleZones: VehicleZone[]): void {
        vehicleZones.forEach(zone => {
            // Limpiar URL para evitar memory leaks
            if (zone.photoUrl && zone.photoUrl.startsWith('blob:')) {
                URL.revokeObjectURL(zone.photoUrl);
            }

            // Resetear propiedades de la zona
            zone.photographed = false;
            zone.photoUrl = undefined;
            zone.photoBlob = undefined;
            zone.timestamp = undefined;
            zone.hasDamage = false;
            zone.damageDescription = '';
        });
    }

    /**
     * Resetea todas las fotos adicionales
     */
    resetAdditionalPhotos(additionalPhotos: AdditionalPhoto[]): AdditionalPhoto[] {
        // Limpiar URLs para evitar memory leaks
        additionalPhotos.forEach(photo => {
            if (photo.url && photo.url.startsWith('blob:')) {
                URL.revokeObjectURL(photo.url);
            }
        });

        return []; // Retornar array vacío
    }

    /**
     * Obtiene los metadatos de una foto
     */
    getPhotoMetadata(zone: VehicleZone | null, zones: VehicleZone[]): string {
        if (!zone) return '';

        const foundZone = zones.find(z => z.id === zone.id);
        if (foundZone && foundZone.photoUrl) {
            return `Capturada • ${this.getPhotoTimestamp()}`;
        }
        return '';
    }

    /**
     * Obtiene el timestamp formateado de una foto
     */
    getPhotoTimestamp(): string {
        const now = new Date();
        return `${this.dateFormatLocalization.formatDate(now)} ${now.toLocaleTimeString(this.dateFormatLocalization.getCurrentConfig().locale, {
            hour: '2-digit',
            minute: '2-digit'
        })}`;
    }

    hasPhotoDetails(zoneId: string | null): boolean {
        return !!zoneId;
    }

    deleteAdditionalPhotoByIndex(
        additionalPhotos: AdditionalPhoto[],
        index: number
    ): AdditionalPhoto[] {
        const photo = additionalPhotos[index];
        if (photo) {
            if (photo.url) {
                this.revokeObjectURL(photo.url);
            }
            additionalPhotos.splice(index, 1);
        }
        return additionalPhotos;
    }

    deleteDocumentPhotoById(
        vehicleDocuments: VehicleDocument[],
        documentId: string
    ): boolean {
        const document = vehicleDocuments.find(doc => doc.id === documentId);
        if (document) {
            this.cleanPhotoData(document);
            return true;
        }
        return false;
    }
}
